import { RefreshCw } from 'lucide-react';
import type React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useAppContext } from '../contexts/AppContext';

interface ApiEndpointSelectorProps {
    // Health status
    apiHealthStatus: Record<
        string,
        { status: 'checking' | 'online' | 'offline'; version?: string }
    >;
    onRefreshHealthChecks: () => void;
    isRefreshingHealth: boolean;
}

const ApiEndpointSelector: React.FC<ApiEndpointSelectorProps> = ({
    apiHealthStatus,
    onRefreshHealthChecks,
    isRefreshingHealth,
}) => {
    const {
        availableEndpoints,
        selectedEndpoint,
        customEndpoint,
        useCustomEndpoint,
        setSelectedEndpoint,
        setCustomEndpoint,
        setUseCustomEndpoint,
        getCurrentEndpointId,
    } = useAppContext();

    const handleEndpointChange = (endpointId: string) => {
        if (endpointId === 'custom') {
            setUseCustomEndpoint(true);
        } else {
            const endpoint = availableEndpoints.find((e) => e.id === endpointId);
            if (endpoint) {
                setSelectedEndpoint(endpoint);
            }
        }
    };

    return (
        <>
            {/* API Endpoint Selection */}
            <div className="mb-2">
                <div className="flex items-center justify-between mb-1">
                    {/* biome-ignore lint/a11y/noLabelWithoutControl: label is visually associated with the select below */}
                    <label className="text-xs font-medium">🌐 API Endpoint:</label>
                    <Button
                        onClick={onRefreshHealthChecks}
                        disabled={isRefreshingHealth}
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-xs"
                    >
                        <RefreshCw
                            className={`h-3 w-3 mr-1 ${isRefreshingHealth ? 'animate-spin' : ''}`}
                        />
                        {isRefreshingHealth ? 'Refreshing...' : 'Refresh'}
                    </Button>
                </div>
                <Select value={getCurrentEndpointId()} onValueChange={handleEndpointChange}>
                    <SelectTrigger className="w-full h-8">
                        <SelectValue placeholder="Select an endpoint" />
                    </SelectTrigger>
                    <SelectContent
                        className="z-[9999]"
                        position="popper"
                        side="bottom"
                        align="start"
                    >
                        {/* Predefined endpoints */}
                        {availableEndpoints.map((endpoint) => {
                            const status = apiHealthStatus[endpoint.id];
                            const isOnline = status?.status === 'online';
                            const statusIcon = isOnline ? '🟢' : '🔴';
                            const isSelected =
                                !useCustomEndpoint && endpoint.id === selectedEndpoint?.id;

                            return (
                                <SelectItem
                                    key={endpoint.id}
                                    value={endpoint.id}
                                    className={isSelected ? 'bg-blue-50' : ''}
                                >
                                    {statusIcon} {endpoint.name} - {endpoint.url}
                                </SelectItem>
                            );
                        })}

                        {/* Custom endpoint option */}
                        <SelectItem
                            value="custom"
                            className={useCustomEndpoint ? 'bg-blue-50' : ''}
                        >
                            {apiHealthStatus.custom?.status === 'online'
                                ? '🟢'
                                : apiHealthStatus.custom?.status === 'checking'
                                  ? '⏳'
                                  : '🔴'}
                            Custom{customEndpoint ? ` - ${customEndpoint}` : ' - Enter URL below'}
                        </SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Custom Endpoint */}
            <div className="mb-2">
                <div className="flex items-center justify-between mb-1">
                    <label
                        htmlFor="custom-endpoint"
                        className="text-xs font-medium flex items-center gap-1"
                    >
                        <span>🔗</span>
                        Custom API Endpoint
                    </label>
                    <Switch
                        id="custom-endpoint"
                        checked={useCustomEndpoint}
                        onCheckedChange={setUseCustomEndpoint}
                    />
                </div>
                {useCustomEndpoint && (
                    <Input
                        type="text"
                        value={customEndpoint}
                        onChange={(e) => setCustomEndpoint(e.target.value)}
                        placeholder="https://api.example.com"
                        className="w-full h-8"
                    />
                )}
            </div>
        </>
    );
};

export default ApiEndpointSelector;
